<template>
<div class="container mx-auto h-auto mt-40 flex-col flex md:justify-center min-h-0">
  <div class="z-10 md:p-5 laptop:p-0 px-5">
    <MARSHeading>
      {{ $t('features.heading') }}
    </MARSHeading>

    <p class="laptop:w-1/2 mt-4"><NuxtLink :to="$t('menuInfo.platform.path').toString()" :title="$t('menuInfo.platform.title').toString()" class="underline">{{ $t('features.previous_page_overview_1') }}</NuxtLink>
      {{ $t('features.previous_page_overview_2') }}</p>
    <p class="mt-4">{{ $t('features.see_other_features') }}</p>
  </div>
</div>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('features.saas_platform_title') }}</MARSTitle>
  </template>
  <template #col2>
    <p v-html="$t('features.saas_platform_description')"></p>
  </template>
</Block2Cols>

<Block2Cols reverse>
  <template #col1>
    <MARSTitle>{{ $t('features.control_panel_title') }}</MARSTitle>
    <p v-html="$t('features.control_panel_description_1')"></p>
    <p>{{ $t('features.control_panel_description_2') }}</p>
  </template>
  <template #col2>
    <img loading="lazy" src="~/assets/img/My AR Studio Control Panel 1280.webp" :alt="$t('features.image_alt.control_panel')">
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('features.world_wide_distribution_title') }}</MARSTitle>
    <p v-html="$t('features.world_wide_distribution_description')"></p>
  </template>
  <template #col2>
    <div class="flex justify-center">
      <img loading="lazy" src="~/assets/img/ico-world-cloud.svg" :alt="$t('features.image_alt.world_cloud_distribution')">
    </div>
  </template>
</Block2Cols>

<Block2Cols reverse>
  <template #col1>
    <MARSTitle>{{ $t('features.web_3d_ar_title') }}</MARSTitle>
    <p>{{ $t('features.web_3d_ar_description_1') }}</p>
    <ul>
      <li v-html="$t('features.web_3d_ar_share_options.direct_link')"></li>
      <li v-html="$t('features.web_3d_ar_share_options.embed_code')"></li>
      <li v-html="$t('features.web_3d_ar_share_options.qr_code')"></li>
    </ul>
    <p>{{ $t('features.web_3d_ar_description_2') }}</p>
  </template>
  <template #col2>
    <div class="flex justify-center">
      <img loading="lazy" src="~/assets/img/web-3d-ar-620.webp" :alt="$t('features.image_alt.web_3d_ar_viewer')">
    </div>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('features.pbr_materials_title') }}</MARSTitle>
    <p>{{ $t('features.pbr_materials_description_1') }}</p>
    <p v-html="$t('features.pbr_materials_description_2')"></p>
  </template>
  <template #col2>
    <div class="flex justify-center">
      <img loading="lazy" src="~/assets/img/PBR-materials.webp" :alt="$t('features.image_alt.pbr_materials')">
    </div>
  </template>
</Block2Cols>

<Block1Col>
  <MARSTitle>{{ $t('features.real_world_scale_title') }}</MARSTitle>
</Block1Col>
<Block2Cols compact>
  <template #col1>
    <p v-html="$t('features.real_world_scale_description_1')"></p>
    <p>{{ $t('features.real_world_scale_description_2') }}</p>
    <ul>
      <li>{{ $t('features.real_world_scale_features.feature_1') }}</li>
      <li>{{ $t('features.real_world_scale_features.feature_2') }}</li>
      <li>{{ $t('features.real_world_scale_features.feature_3') }}</li>
      <li>{{ $t('features.real_world_scale_features.feature_4') }}</li>
    </ul>
    <p>{{ $t('features.double_check_dimensions') }}</p>
  </template>
  <template #col2>
    <div class="w-full h-iframeContainer md:h-iframeContainerMd lg:mt-0 viewer3d" style="height: 550px; border: 1px solid lightgray;">
      <iframe 
        loading="lazy" 
        title="My AR Studio Universal Viewer: example of showing physical dimensions" 
        allow="xr-spatial-tracking *;fullscreen *; web-share *" 
        allowfullscreen 
        allowtransparency="true" 
        style="width:100%; height:100%; border: 0" 
        :src="'https://cdn.myarstudio.cloud/f2f01834-6984-4e4d-bd20-8e7586523cf0/Playground/Products/86d6b5d1-ed63-5a2d-8018-f214d5c3fc41/default/bb0f623e-77cd-528d-a172-6f2de74c6932/index.html?language=' + $i18n.locale"
      />
    </div>
  </template>
</Block2Cols>

<Block2Cols items-bottom>
  <template #col1>
    <MARSTitle>{{ $t('features.configurations_title') }}</MARSTitle>
    <p v-html="$t('features.configurations_description_1')"></p>
    <p>{{ $t('features.configurations_description_2') }}</p>
    <p class="text-lightgray italic pt-8" v-html="$t('features.lazy_load_hint')"></p>
    <p>{{ $t('features.click_to_view') }}</p>
    <div>
      <picture>
        <source type="image/webp" srcset="~/assets/img/landing/UniversalViewerClickToViewIn3D.webp">
        <source type="image/png" srcset="~/assets/img/landing/UniversalViewerClickToViewIn3D.png">
        <img loading="lazy" style="height: 60px;" src="~/assets/img/landing/UniversalViewerClickToViewIn3D.png" :alt="$t('features.image_alt.lazy_loading_button')">
      </picture>
    </div>
    <div class="flex mt-8 items-center">
      <p>{{ $t('features.click_product_selector') }}</p>
      <div>
        <picture>
          <source type="image/webp" srcset="~/assets/img/landing/ProductSelectorButton.webp">
          <source type="image/png" srcset="~/assets/img/landing/ProductSelectorButton.png">
          <img loading="lazy" style="width: 130px;" src="~/assets/img/landing/ProductSelectorButton.png" :alt="$t('features.image_alt.product_selector_button')">
        </picture>
      </div>
    </div>
  </template>
  <template #col2>
    <div class="w-full h-iframeContainer md:h-iframeContainerMd lg:mt-0 viewer3d" style="min-height: 450px; border: 1px solid lightgray;">
    <iframe
          loading="lazy"
          title="My AR Studio Universal Viewer: example of the Product Selector feature."
          allow="xr-spatial-tracking *;fullscreen *; web-share *"
          allowfullscreen
          allowtransparency="true"
          style="width:100%; height:100%; border: 0"
          :src="'https://cdn.myarstudio.cloud/f2f01834-6984-4e4d-bd20-8e7586523cf0/Playground/Products/86d6b5d1-ed63-5a2d-8018-f214d5c3fc41/default/index.html?s=y&language=' + $i18n.locale"
        />
    </div>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <p>{{ $t('features.create_variants') }}</p>
    <p v-html="$t('features.simple_configurators')"></p>
  </template>
  <template #col2>
    <div class="flex justify-center">
      <picture>
        <source type="image/webp" srcset="~/assets/img/landing/ProductSelectorEditor.webp">
        <source type="image/jpeg" srcset="~/assets/img/landing/ProductSelectorEditor.jpg">
        <img loading="lazy" src="~/assets/img/landing/ProductSelectorEditor.jpg" :alt="$t('features.image_alt.product_selector_editor')">
      </picture>
    </div>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('features.annotations_title') }}</MARSTitle>
    <p v-html="$t('features.annotations_description_1')"></p>
    <p v-html="$t('features.annotations_description_2')"></p>

    <p>{{ $t('features.annotations_hint') }}</p>
    <ul>
      <li>{{ $t('features.annotations_controls.open_annotation_1') }}
        <img loading="lazy" style="height: 25px;" class="inline" src="~/assets/img/landing/UniversalViewerHotspotPlus.png" :alt="$t('features.image_alt.annotation_expand_button')">
        {{ $t('features.annotations_controls.open_annotation_2') }}</li>
      <li>{{ $t('features.annotations_controls.close_annotation_1') }} <img loading="lazy" style="height: 25px;" class="inline" src="~/assets/img/landing/UniversalViewerHotspotMinus.png" :alt="$t('features.image_alt.annotation_collapse_button')">
        {{ $t('features.annotations_controls.close_annotation_2') }}</li>
      <li>{{ $t('features.annotations_controls.toggle_menu_1') }}
        <img loading="lazy" style="height: 25px;" class="inline" src="~/assets/img/landing/UniversalViewerMenuButton.png" :alt="$t('features.image_alt.annotation_toggle_menu_button')">
        {{ $t('features.annotations_controls.toggle_menu_2') }}</li>
    </ul>
  </template>
  <template #col2>
    <div class="w-full h-iframeContainer md:h-iframeContainerMd lg:mt-0 viewer3d" style="min-height: 400px; border: 1px solid lightgray;">
      <iframe 
        loading="lazy" 
        title="My AR Studio Universal Viewer: example of the Hotspots feature." 
        allow="xr-spatial-tracking *;fullscreen *; web-share *" 
        allowfullscreen 
        allowtransparency="true" 
        style="width:100%; height:100%; border: 0" 
        :src="'https://cdn.myarstudio.cloud/f2f01834-6984-4e4d-bd20-8e7586523cf0/Playground/Products/7bd87dfc-e0c4-5bb5-99e4-62d0f41bef57/default/22d8a000-11ad-5a23-858c-3d39c54fea58/index.html?language=' + $i18n.locale"
      />
    </div>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <p v-html="$t('features.annotations_editor')"></p>
  </template>
  <template #col2>
    <picture>
      <source type="image/webp" srcset="~/assets/img/landing/HotspotEditor.webp">
      <source type="image/jpeg" srcset="~/assets/img/landing/HotspotEditor.jpg">
      <img loading="lazy" src="~/assets/img/landing/HotspotEditor.jpg" :alt="$t('features.image_alt.hotspot_editor')">
    </picture>
  </template>
</Block2Cols>

<Block2Cols items-end>
  <template #col1>
    <MARSTitle>{{ $t('features.hdr_lighting_title') }}</MARSTitle>
    <p v-html="$t('features.hdr_lighting_description_1')"></p>
  </template>
  <template #col2>
    <p>{{ $t('features.hdr_lighting_description_2') }}</p>
    <p>{{ $t('features.hdr_lighting_description_3') }}</p>
  </template>
</Block2Cols>

<Block1Col compact class="mt-8">
  <div class="w-full">
    <picture>
      <source type="image/webp" srcset="~/assets/img/landing/HDR_horizontal.webp">
      <source type="image/jpeg" srcset="~/assets/img/landing/HDR_horizontal.jpg">
      <img loading="lazy" class="w-full" src="~/assets/img/landing/HDR_horizontal.jpg" :alt="$t('features.image_alt.hdr_lighting')">
    </picture>
  </div>
</Block1Col>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('features.analytics_title') }}</MARSTitle>
    <p v-html="$t('features.analytics_description')"></p>
  </template>
  <template #col2>
    <img loading="lazy" src="~/assets/img/analytics.webp" :alt="$t('features.image_alt.analytics_dashboard')">
  </template>
</Block2Cols>
</template>

<script lang="ts" setup>
import { useIubenda } from 'mars-iubenda-vite';
const {youtubeEnabled} = useIubenda()
const panelLinks = usePanelLinks()

</script>

<style scoped>

</style>
